import { Column } from "primereact/column";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import localeText from "../../../../locale";
import CatalogTable from "../../components/list/CatalogTable";
import StatusBadge from "../../components/list/StatusBadge";
import useAdminList from "../../hooks/useAdminList";
import { buildSearchText } from "../../utils/adminUtils";
import { money } from "../../../Channel/channelUtils";
import { callChannel, channelService, defaultPeriod, downloadCsv, formatDateTime } from "./adminScontiPosteUtils";
import PeriodFilter from "./PeriodFilter";

// Estorno entra negativo nos totais
const signedAmount = (entry) => (entry.type === "REVERSED" ? -1 : 1) * (Number(entry.amount) || 0);

const toRow = (entry) => ({
  ...entry,
  dateValue: entry.createdDate ? new Date(entry.createdDate).getTime() : 0,
  amountValue: signedAmount(entry),
  searchText: buildSearchText([String(entry.orderId), entry.seller, entry.sellerName, entry.buyer]),
});

// Provisao do cashback ScontiPoste: o que foi descontado dos vendedores, para conferir com a fatura da Poste Italiane
const ChannelCashbackSection = () => {
  const [period, setPeriod] = useState(defaultPeriod);
  const { items, loading, loadError, reload } = useAdminList(async () =>
    (await callChannel(() => channelService.adminCashback(period))) || [],
  );

  const rows = useMemo(() => items.map(toRow), [items]);
  const total = rows.reduce((sum, row) => sum + row.amountValue, 0);

  const bySeller = useMemo(() => {
    const totals = {};
    rows.forEach((row) => {
      const key = row.seller || "—";
      totals[key] = totals[key] || { name: row.sellerName || key, amount: 0 };
      totals[key].amount += row.amountValue;
    });
    return Object.values(totals).sort((a, b) => b.amount - a.amount);
  }, [rows]);

  const exportCsv = () =>
    downloadCsv(
      `scontiposte-cashback-${period.from}-${period.to}.csv`,
      ["date", "type", "order", "checkout", "seller", "buyer", "items_value", "cashback", "currency"],
      rows.map((row) => [
        formatDateTime(row.createdDate),
        row.type,
        row.orderId,
        row.checkoutId,
        row.seller,
        row.buyer,
        Number(row.orderAmount || 0).toFixed(2),
        row.amountValue.toFixed(2),
        row.currency,
      ]),
    );

  return (
    <div className="admin-stack admin-stack--gap">
      <p className="admin-scp__help" style={{ margin: 0 }}>
        {localeText("adminChannelCashbackHelp")}
      </p>

      <PeriodFilter value={period} onChange={setPeriod} onApply={reload} loading={loading}>
        <button type="button" className="admin-btn" onClick={exportCsv} disabled={loading || rows.length === 0}>
          <i className="pi pi-download" aria-hidden="true" /> {localeText("adminChannelExportCsv")}
        </button>
      </PeriodFilter>

      {!loading && (
        <div className="admin-stack">
          <p className="admin-scp__summary admin-strong">
            {localeText("adminChannelCashbackTotal", { total: money(total, "EUR") })}
          </p>
          {bySeller.length > 0 && (
            <ul className="admin-scp__sellers-total" aria-label={localeText("adminChannelBySeller")}>
              {bySeller.map((seller) => (
                <li key={seller.name}>
                  <span className="admin-muted">{seller.name}:</span> <span className="admin-num">{money(seller.amount, "EUR")}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <CatalogTable
        rows={rows}
        loading={loading}
        loadError={loadError}
        onReload={reload}
        searchPlaceholder={localeText("adminChannelSearchCashback")}
        sortField="dateValue"
        sortOrder={-1}
        minWidth="52rem"
      >
        <Column
          header={localeText("adminChannelDate")}
          field="dateValue"
          sortable
          body={(row) => formatDateTime(row.createdDate)}
        />
        <Column
          header={localeText("adminChannelOrder")}
          field="orderId"
          sortable
          body={(row) => (
            <Link to={`/dashboard-purchasesdetails?orderId=${row.orderId}`} className="admin-link-btn">
              #{row.orderId}
            </Link>
          )}
        />
        <Column
          header={localeText("adminChannelSeller")}
          field="sellerName"
          sortable
          body={(row) => (
            <div className="admin-stack">
              <span className="admin-strong">{row.sellerName}</span>
              <span className="admin-muted">@{row.seller}</span>
            </div>
          )}
        />
        <Column
          header={localeText("adminChannelOrderAmount")}
          field="orderAmount"
          sortable
          body={(row) => <span className="admin-num">{money(row.orderAmount, row.currency || "EUR")}</span>}
        />
        <Column
          header={localeText("adminChannelAmount")}
          field="amountValue"
          sortable
          body={(row) => <span className="admin-num admin-strong">{money(row.amountValue, row.currency || "EUR")}</span>}
        />
        <Column
          header={localeText("adminChannelStatus")}
          field="type"
          body={(row) => (
            <StatusBadge tone={row.type === "REVERSED" ? "warning" : "good"}>
              {localeText(`adminChannelEntry${row.type}`) || row.type}
            </StatusBadge>
          )}
        />
      </CatalogTable>
    </div>
  );
};

export default ChannelCashbackSection;
